import {
  ActivityLog,
  File,
  MergeTransactionIds,
  Transaction,
  type TransactionDocument,
} from '@kalos/kalos-rpc';
import { Button, Dialog, DialogContent, useAlert } from '@kalos/ui';
import { format } from 'date-fns';
import { type FC, useCallback, useState } from 'react';

import { useAuth } from '../../../context/AuthContext';
import {
  ActivityLogClientService,
  FileClientService,
  S3ClientService,
  TransactionActivityClientService,
  TransactionClientService,
  TransactionDocumentClientService,
} from '../../../tools/helpers';
import { Loader } from '../../Loader/main';
import { MergeTable, type SelectedChoice } from '../MergeTable';
import { TransactionTable } from '../TransactionTable';

/*
  Compares transactions with each other and has the ability to create a "diff view" sort of table which shows conflicts in the
  transactions. The user can click on the buttons to resolve the conflicts by choosing the correct option for them, and they can
  modify their choices in the transaction viewer if they so choose before they upload.
*/

interface Props {
  onMerge?: () => void;
  selectionLimit: number;
}

// Conflicts are checked before they are created, and if there is one with the same index on it then
// the new one will not be made and instead the old conflict will have the new transaction pushed
// into it.
interface Conflict {
  index: number; // index of the fields that are conflicting
  transactionsAffected: Transaction[]; // The transactions that are conflicting with each other
}

// The names displayed in the rows of the table (database name as key, display name as value)
const ProperTransactionNames = {
  id: 'Id',
  jobId: 'Job ID',
  departmentIdList: 'Department ID List',
  departmentId: 'Department',
  ownerId: 'Purchaser',
  vendor: 'Vendor',
  costCenterId: 'Cost Center',
  description: 'Description',
  amount: 'Amount',
  timestamp: 'Timestamp',
  notes: 'Notes',
  isActive: 'Active?',
  statusId: 'Status ID',
  status: 'Status',
  ownerName: 'Owner Name',
  cardUsed: 'Card Used',
  documents: 'Documents',
  activityLog: 'Activity Log',
  department: 'Transaction Department',
  costCenter: 'Cost Center',
  isAudited: 'Audited?',
  isRecorded: 'Recorded?',
  artificialId: 'Artificial ID',
  vendorCategory: 'Vendor Category',
  assignedEmployeeId: 'Creator',
  assignedEmployeeName: 'Creator Name',
  transactionLineItems: 'Line Items',
};
const transactionSchema = {
  jobId: { type: 'number', defaultValue: 0 },
  departmentId: { type: 'number', defaultValue: 0 },
  ownerId: { type: 'number', defaultValue: 0 },
  vendor: { type: 'string', defaultValue: '' },
  costCenterId: { type: 'number', defaultValue: 0 },
  description: { type: 'string', defaultValue: '' },
  amount: { type: 'number', defaultValue: 0 },
  timestamp: { type: 'string', defaultValue: '' },
  notes: { type: 'string', defaultValue: '' },
  statusId: { type: 'number', defaultValue: 0 },
  isAudited: { type: 'number', defaultValue: 0 },
  isRecorded: { type: 'number', defaultValue: 0 },
  vendorCategory: { type: 'string', defaultValue: '' },
  assignedEmployeeId: { type: 'number', defaultValue: 0 },
  // Add more properties here
};

function transformObject(obj: any, schema: any) {
  const transformedObj: { [key: string]: any } = {};
  for (const key in schema) {
    if (Object.hasOwnProperty.call(obj, key)) {
      const value = obj[key];

      switch (schema[key].type) {
        case 'number':
          transformedObj[key] = parseFloat(value) || schema[key].defaultValue;
          break;
        case 'string':
          transformedObj[key] = String(value);
          break;
        // Add more cases for other data types if needed
        default:
          transformedObj[key] = value;
      }
    } else {
      transformedObj[key] = schema[key].defaultValue;
    }
  }

  // Copy any remaining properties from obj to transformedObj
  for (const key in obj) {
    if (!Object.hasOwnProperty.call(transformedObj, key)) {
      transformedObj[key] = obj[key];
    }
  }

  return transformedObj;
}

// The fields to ignore when generating conflicts. This will prevent the user from setting any value for these when upserting the
// merged transaction to the database.
const IgnoredFieldNames: string[] = [
  'activityLogString',
  'departmentString',
  'ownerName',
  'assignedEmployeeName',
  'id',
  'costCenterString',
  'activityLogList',
  'cardUsed',
  'artificialId',
  'activityLog',
  'vendorId',
  'projectId',
];

export const CompareTransactions: FC<Props> = ({ onMerge, selectionLimit }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const loggedUserId = useAuth().user.id;
  const [conflicts, setConflicts] = useState<Conflict[]>([]);

  const [transactionToSave, setTransactionToSave] = useState<Transaction>(Transaction.create());

  const alert = useAlert();

  const showErrorAlert = useCallback(
    (error: string) => {
      alert({
        title: 'Error',
        body: (
          <>
            <p>
              An error occurred during the saving of the transaction. Please notify one of our
              web-team members
            </p>
            <p>{error}</p>
          </>
        ),
      });
    },
    [alert],
  );

  const [loading, setLoading] = useState<boolean>(false);

  // Deletes all selected transactions
  const deleteTransactions = useCallback(async () => {
    if (!transactions) {
      return;
    }
    for await (const txn of transactions) {
      try {
        setLoading(true);
        const tempTxn = Transaction.clone(txn);
        // Delete is not working atm, leaving a todo here for future reference but using update as a replacement
        // TODO
        console.log(tempTxn);
        tempTxn.isActive = 0;
        tempTxn.fieldMask = ['IsActive'];

        // ? Beginning of ignoring private fields to stop errors "fieldname" in "field_list"

        // txn.ownerName = undefined;
        // txn.departmentString = undefined;
        // txn.activityLogString = undefined;
        // txn.cardUsed = undefined;
        // txn.costCenterString = undefined;
        // txn.assignedEmployeeName = undefined;
        // End of ignoring private fields

        await TransactionClientService.Update(tempTxn);
        setLoading(false);
        // const transactionResult = await TransactionClientService.Delete(txn);
        // if (transactionResult)
        //   console.log('Deleted transaction: ', transactionResult);
      } catch (err) {
        console.error('Failed to delete transaction: ', err);
        if (String(err).includes(`Unknown column`) && String(err).includes(`in 'field list'`))
          console.error(
            `This error commonly occurs when there are fields set on the transaction that do not exist in the database. Please ensure they are undefined.`,
          );
        setLoading(false);
      }
    }
  }, [transactions]);

  const handleSaveActivityLog = useCallback(
    async (activityLog: ActivityLog) => {
      try {
        await ActivityLogClientService.Create(activityLog);
      } catch (err) {
        console.error(
          `An error occurred while creating the activity log for the transaction: ${err}`,
        );
        showErrorAlert(err as string);
      }
    },
    [showErrorAlert],
  );

  const handleSetConflicts = useCallback(
    (conflicts: Conflict[]) => setConflicts(conflicts),
    [setConflicts],
  );

  const handleMergeDocuments = useCallback(
    async (mergedTransactionDocumentList: TransactionDocument[][], newTransaction: Transaction) => {
      for (let i = 0; i < mergedTransactionDocumentList.length; i++) {
        const documents = mergedTransactionDocumentList[i];
        for (let j = 0; j < documents.length; j++) {
          //Step 1- Change Transaction Id
          try {
            const req = documents[j];
            const oldTransaction = req.transactionId;
            req.transactionId = newTransaction.id;
            req.fieldMask = ['TransactionId'];
            await TransactionDocumentClientService.Update(req);
            //Step 2- Change Name in File Table (change old Job Number to New Job Number)
            const fileReq = File.create();
            fileReq.id = req.fileId;
            const fileResults = await FileClientService.Get(fileReq);
            if (fileResults) {
              const updateFileReq = File.create();
              updateFileReq.id = fileResults!.id;
              const oldName = fileResults!.name;
              const newName = oldName.replace(
                oldTransaction.toString(),
                newTransaction.id.toString(),
              );
              updateFileReq.name = newName;
              updateFileReq.fieldMask = ['Name'];
              await FileClientService.Update(updateFileReq);
              // Move Document in S3, changing Name to New Name
              await S3ClientService.Move(
                { bucket: 'kalos-transactions', key: oldName },
                { bucket: 'kalos-transactions', key: newName },
              );
            }
          } catch (err) {
            console.error(`An error occurred merging documents: ${err}`);
            showErrorAlert(err as string);
          }
        }
      }
    },
    [showErrorAlert],
  );

  const handleSaveTransaction = useCallback(
    async (transaction: Transaction) => {
      try {
        setLoading(true);
        const tempTransaction = transformObject(transaction, transactionSchema);
        transaction = Transaction.clone(tempTransaction as Transaction);

        const txnMade = await TransactionClientService.Create(transaction);
        if (txnMade.id != 0) {
          const docList: TransactionDocument[][] = [];
          for (let i = 0; i < transactions!.length; i++) {
            const result = await TransactionDocumentClientService.byTransactionID(
              transactions![i].id,
            );
            docList.push(result!);
          }
          await handleMergeDocuments(docList, txnMade);
          const activityLog = ActivityLog.create();
          activityLog.activityName = `Merged Transactions - IDs: ${transactions!
            .map((txn) => txn.id)
            .join(' AND ')} - Created transaction with ID: ${txnMade.id}`;
          activityLog.userId = loggedUserId;
          activityLog.activityDate = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
          const mergeReq = MergeTransactionIds.create();
          mergeReq.mergedTransaction = txnMade.id;
          mergeReq.transactionIdList = transactions!.map((transaction) => transaction.id);
          TransactionActivityClientService.MergeTransactionLogs(mergeReq);
          await deleteTransactions();

          await handleSaveActivityLog(activityLog);
        } else {
          console.error(
            `An error occurred while creating the transaction, no action has been taken`,
          );
        }
        setLoading(false);
        if (onMerge) onMerge();
        return txnMade.id;
      } catch (err) {
        console.error(`An error occurred while saving the transaction: ${err as string}`, err);
        showErrorAlert(String(err));
        return 0;
      }
    },
    [
      onMerge,
      handleMergeDocuments,
      transactions,
      loggedUserId,
      deleteTransactions,
      handleSaveActivityLog,
      showErrorAlert,
    ],
  );

  const [transactionTableKey, setTransactionTableKey] = useState<number>(0);
  const handleSetSubmissionResults = useCallback(
    async (submissionResults: SelectedChoice[]) => {
      submissionResults.forEach(async (result) => {
        const tempFieldName = result.fieldName.charAt(0).toLowerCase() + result.fieldName.slice(1);
        // @ts-ignore
        transactionToSave[tempFieldName] = result.value;
        console.log('fieldname', tempFieldName);
      });

      setLoading(true);

      await handleSaveTransaction(transactionToSave);
      setTransactions([]);
      // to reset the transaction table
      setTransactionTableKey((prev) => prev + 1);
      setLoading(false);
      setConflicts([]);
    },
    [transactionToSave, setLoading, setConflicts, handleSaveTransaction],
  );

  const handleSetTransactionToSave = useCallback(
    (transactionToSaveNew: Transaction) => {
      setTransactionToSave(transactionToSaveNew);
    },
    [setTransactionToSave],
  );

  const generateConflicts = useCallback(() => {
    if (!transactions) {
      console.error('There are no transactions to generate conflicts from.');
      return [];
    }

    const newConflicts: Conflict[] = [];
    const newTransaction = Transaction.create();

    transactions.forEach((transaction, index) => {
      if (transactions[index - 1] != null) {
        // Loop over fields in the current transaction and compare that with the fields in the previous transaction at the same index
        let fieldIndex = 0;
        const previousTransaction = Object.values(transactions[index - 1]);

        const keys = Object.keys(transaction);
        for (const fieldCurrent of Object.values(transaction)) {
          if (IgnoredFieldNames.includes(keys[fieldIndex])) {
            fieldIndex++;
            continue;
          }

          let fieldCurrentEmpty = false;
          let fieldPreviousEmpty = false;
          const fieldPrevious = previousTransaction[fieldIndex]; // For namings' sake
          // If either of them is a '' or a null, we need to determine the one that is not null and choose that value for the merge
          // This regex determines if whitespace exists in the string
          if (fieldCurrent?.toString().match(/^\s+$/) || fieldCurrent.toString() == '') {
            fieldCurrentEmpty = true;
          }
          if (fieldPrevious?.toString().match(/^\s+$/) || fieldPrevious.toString() == '') {
            fieldPreviousEmpty = true;
          }

          if (fieldCurrentEmpty && !fieldPreviousEmpty) {
            // Set the field to be the previous field
            //@ts-ignore
            newTransaction[keys[fieldIndex]] = fieldPrevious;
            fieldIndex++;
            continue;
          }
          if (fieldPreviousEmpty && !fieldCurrentEmpty) {
            // Set the field to be the current field
            //@ts-ignore
            newTransaction[keys[fieldIndex]] = fieldCurrent;
            fieldIndex++;
            continue;
          }
          if (fieldPreviousEmpty && fieldCurrentEmpty) {
            // Set the field empty
            //@ts-ignore
            newTransaction[keys[fieldIndex]] = '';

            fieldIndex++;
            continue;
          }

          if (fieldCurrent == fieldPrevious) {
            console.log('we are fine, we got the same');

            // Set the field to the value of the current field
            //@ts-ignore
            newTransaction[keys[fieldIndex]] = fieldCurrent;
          }
          if (
            fieldPrevious != fieldCurrent &&
            fieldPrevious?.toString() != fieldCurrent?.toString()
          ) {
            let newConflict: Conflict[];

            // need to find conflicts with the same indices and then see if the members match
            // eslint-disable-next-line no-loop-func
            if (newConflicts.filter((conflict) => conflict.index === fieldIndex).length > 0) {
              newConflict = newConflicts
                // eslint-disable-next-line no-loop-func
                .filter((conflict) => conflict.index === fieldIndex)
                .map((conflictMatching) => {
                  if (!conflictMatching.transactionsAffected.includes(transaction)) {
                    conflictMatching.transactionsAffected.push(transaction);
                  }
                  if (!conflictMatching.transactionsAffected.includes(transactions[index - 1])) {
                    conflictMatching.transactionsAffected.push(transactions[index - 1]);
                  }

                  return conflictMatching;
                });
            }
            // eslint-disable-next-line no-loop-func
            if (newConflicts.filter((conflict) => conflict.index == fieldIndex).length == 0) {
              // At this point, we need to resolve the conflict at a later point so we'll shove it into a conflict.
              newConflicts.push(
                newConflict!
                  ? newConflict![0]
                  : ({
                      index: fieldIndex,
                      transactionsAffected: [transaction, transactions[index - 1]],
                    } as Conflict),
              );
            }
          }

          fieldIndex++;
        }
      }
    });
    console.log('we finished comparing');
    console.log('our new transaction', Transaction.clone(newTransaction));
    return [newConflicts, newTransaction];
  }, [transactions]);

  const handleMerge = useCallback(() => {
    if (!transactions) {
      console.error('Cannot handle merge - no transactions available to merge.');
      return;
    }
    const [conflicts, transaction] = generateConflicts();
    if ((conflicts as Conflict[]).length === 0)
      alert({
        title: 'Transaction merge conflict',
        body: 'The transactions are the same. There is nothing to merge.',
      });
    setConflicts(conflicts as Conflict[]);
    setTransactionToSave(transaction as Transaction);
  }, [transactions, generateConflicts, alert]);

  return (
    <>
      {loading && <Loader />}

      {conflicts && (
        <Dialog
          open={conflicts.length > 0}
          onOpenChange={(isOpen) => {
            if (!isOpen) handleSetConflicts([]);
          }}
        >
          <DialogContent showClose={false} className="max-h-[80vh] min-w-[80vw] overflow-auto p-0">
            <MergeTable
              transaction={transactionToSave}
              viewMergedTransaction
              onSaveMergedTransaction={(saved) => {
                const txn = Transaction.create();

                txn.jobId = saved.jobId;
                txn.departmentId = saved.departmentId;
                txn.ownerId = saved.ownerId;
                txn.vendor = saved.vendor;
                txn.costCenterId = saved.costCenterId;
                txn.description = saved.description;
                txn.amount = saved.amount;
                txn.timestamp = saved.timestamp;
                txn.notes = saved.notes;
                txn.isActive = saved.isActive;
                txn.statusId = saved.statusId;
                txn.status = saved.status;
                txn.ownerName = saved.ownerName;
                txn.cardUsed = saved.cardUsed;
                txn.isAudited = saved.isAudited;
                txn.isRecorded = saved.isRecorded;
                txn.vendorCategory = saved.vendorCategory;
                txn.assignedEmployeeId = saved.assignedEmployeeId;
                txn.assignedEmployeeName = saved.assignedEmployeeName;
                txn.invoiceNumber = saved.invoiceNumber;
                txn.projectId = saved.projectId;
                handleSetTransactionToSave(txn);
              }}
              onChangeTransaction={(newTxn) => {
                newTxn.vendorId =
                  transactions.find((txn) => txn.vendor === newTxn.vendor)?.vendorId || 0;
                handleSetTransactionToSave(newTxn);
              }}
              columnHeaders={[{ name: 'Name of Field' }]}
              rows={conflicts.map((conflict) => {
                const keys = Object.keys(conflict.transactionsAffected[0]);
                return {
                  rowName: keys[conflict.index],
                  rowIndex: conflict.index,
                  choices: conflict.transactionsAffected.map((txn) => {
                    // @ts-ignore
                    const cast = txn[keys[conflict.index]];
                    if (Array.isArray(cast) && cast.length > 0 && typeof cast[0] === 'object') {
                      return `${cast.length} item${cast.length > 1 ? 's' : ''}`;
                    }
                    return `${cast}`;
                  }),
                };
              })}
              onSubmit={handleSetSubmissionResults}
              onCancel={() => handleSetConflicts([])}
              properNames={ProperTransactionNames}
              loading={loading}
            />
          </DialogContent>
        </Dialog>
      )}

      <div>
        <TransactionTable
          stickyHeaderSlot={
            <div className="flex flex-col flex-wrap gap-2 rounded-lg bg-slate-200 p-2 pr-8 md:flex-row md:gap-4 md:p-4">
              <h1>Select Transaction To Merge</h1>
              <Button
                disabled={transactions.length !== selectionLimit}
                size="sm"
                onClick={handleMerge}
              >
                {transactions.length === selectionLimit
                  ? 'Merge Selected Transactions'
                  : `Select ${selectionLimit - transactions.length} More Transaction${transactions.length === selectionLimit - 1 ? '' : 's'}`}
              </Button>
              <Button
                size="sm"
                disabled={!transactions.length}
                onClick={() => {
                  setTransactionTableKey((prev) => prev + 1);
                  setTransactions([]);
                }}
              >
                Deselect transactions
              </Button>
            </div>
          }
          isSelector
          key={transactionTableKey}
          setTransactions={setTransactions}
          selectionLimit={selectionLimit}
        />
      </div>
    </>
  );
};
